import React, { useEffect, useState } from "react";
import { DataGrid } from "@material-ui/data-grid";
import {
  Grid,
  IconButton,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Paper,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import Star from "@material-ui/icons/Star";
import StarHalf from "@material-ui/icons/StarHalf";
import StarBorder from "@material-ui/icons/StarBorder";
import StarOutlinedIcon from "@material-ui/icons/StarOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import FiberNewIcon from "@material-ui/icons/FiberNew";
import { makeStyles } from "@material-ui/core/styles";
import { supabase } from "../../../../../supabase";
import { useAuth } from "../../../../../contexts/AuthContext";
import ModalCard from "../../../../shared/ModalCard";
import ViewLead from "../../../../admin-section/admin-sales-leads/ViewLead";
import AddLeadModal from "./AddLeadModal";
import sqlToStandardDate from "../../../../shared/functions/sqlToStandardDate";
import formatTicketNumber from "../../../../shared/functions/formatTicketNumber";
// Adjust this to handle both Agent and Sales Assist.
import RateAgentAssistServiceModal from "../shared/RateAgentAssistServiceModal";

const useStyles = makeStyles(theme => ({
  viewButton: {
    minWidth: "auto",
    padding: theme.spacing(0.5),
  },
}));

/**
 * Checks if there are any unread messages in the activity.
 *
 * @param {Array<{read_user: string|null, read_admin: string|null}>} activity - The array of activity objects, where each object has read status fields.
 * @param {boolean} isAdmin - A flag indicating if the check is for admin (true) or user (false).
 * @returns {boolean} - Returns true if there is at least one unread message, otherwise false.
 */
function hasUnread(activity, isAdmin) {
  const field = isAdmin ? "read_admin" : "read_user";
  return activity.some(item => item[field] === null);
}

/**
 * Component to display a list of leads for a supplier, with the ability to filter
 * by status and view a lead's details.
 *
 * @param {object} props - The component props.
 * @param {object} props.Supplier - The supplier object.
 * @returns {React.ReactElement} - The SalesAssist component.
 */
export default function SalesAssist({ Supplier }) {
  const classes = useStyles();

  // Get the current authenticated user
  const { currentUser } = useAuth();

  // State to manage loading status
  const [loading, setLoading] = useState(false);

  // State to store the list of leads
  const [leads, setLeads] = useState([]);

  // State to manage filter options for displaying leads
  const [displayFilters, setDisplayFilters] = useState({
    open: true,
    inProgress: true,
    closed: false,
  });

  // State to manage the modal for viewing a lead
  const [viewLead, setViewLead] = useState({
    isOpen: false,
    id: null,
  });

  // Function to handle viewing a lead
  const handleViewLead = leadId => {
    setViewLead({ id: leadId, isOpen: true });
  };

  // Function to close the view lead modal
  const closeViewLead = () => {
    setViewLead({ id: null, isOpen: false });
    getData(true);
  };

  const [addLeadModal, setAddLeadModal] = useState(false);

  const toggleAddLeadModal = () => {
    setAddLeadModal(prev => !prev);
    getData();
  };

  const [rateService, setRateService] = useState({
    isOpen: false,
    id: null,
  });

  // Function to fetch leads data from the database
  const getData = async (showLoading = true) => {
    if (showLoading) setLoading(true); // Set loading to true before fetching data

    const { data: salesLeads, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("sales_assist")
      .select("*, sales_assist_activity(read_user, read_admin)")
      .eq("users_master_id", currentUser.id) // Filter by the current user's dealer code
      .eq("supplier", Supplier) // Filter by the supplier
      .eq("is_active", true)
      .order("created_at", { ascending: false }); // Order by date submitted, newest first
    if (error) {
      console.error(error); // Log any errors
    } else {
      const formattedLeads = salesLeads.map(lead => ({
        ...lead,
        ticket_num: formatTicketNumber(lead.ticket_num, "ZSA"),
        unread: hasUnread(lead.sales_assist_activity, currentUser.is_admin),
      }));
      setLeads(formattedLeads); // Update leads state with the fetched data
    }
    if (showLoading) setLoading(false); // Set loading to false after fetching data
  };

  // Function to handle changes to the filter options
  const handleClick = event => {
    const { name } = event.target;
    setDisplayFilters(old => ({
      ...old,
      [name]: !old[name],
    }));
  };

  // Filter leads based on the selected filter options
  const openFilter = displayFilters.open ? "Open" : undefined;
  const inProgressFilter = displayFilters.inProgress
    ? "In Progress"
    : undefined;
  const closedFilter = displayFilters.closed ? "Closed" : undefined;

  const displayLeads = leads?.filter(
    lead =>
      lead.status === openFilter ||
      lead.status === inProgressFilter ||
      lead.status === closedFilter ||
      lead.status === null
  );

  const toggleRateServiceModal = ticketId => {
    setRateService({ id: ticketId, isOpen: true });
  };

  const closeRateServiceModal = () => {
    setRateService({ id: null, isOpen: false });
    getData(true);
  };

  // Columns configuration for the DataGrid
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      renderCell: params => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Tooltip title="View ticket details">
            <IconButton
              size="small"
              onClick={() => handleViewLead(params.id)}
              style={{ color: params.row.unread ? "#f44336" : "#3f51b5" }}
            >
              {params.row.unread ? <FiberNewIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
          {params.row.status === "Closed" &&
            params.row.rating_star === null && (
              <Tooltip title="Rate Service">
                <div style={{ marginLeft: 10 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.viewButton}
                    onClick={() => toggleRateServiceModal(params.id)}
                  >
                    <StarOutlinedIcon fontSize="small" />
                  </Button>
                </div>
              </Tooltip>
            )}
          {params.row.status === "Closed" && params.row.rating_star && (
            <Tooltip
              title={`Your Review: ${params.row.rating_star}/5.\n\n${params.row.rating_comment}`}
            >
              <div style={{ marginLeft: 10 }}>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.viewButton}
                  disabled
                >
                  {params.row.rating_star <= 2 && (
                    <StarBorder fontSize="small" />
                  )}
                  {params.row.rating_star > 2 &&
                    params.row.rating_star <= 4 && (
                      <StarHalf fontSize="small" />
                    )}
                  {params.row.rating_star > 4 && <Star fontSize="small" />}
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      field: "ticket_num",
      headerName: "Ticket Number",
      width: 130,
      renderCell: params => (
        <Chip
          label={params.value}
          size="small"
          style={{
            backgroundColor: params.row.unread ? "#ffebee" : undefined,
            fontWeight: params.row.unread ? 600 : undefined,
          }}
        />
      ),
    },
    { field: "date", headerName: "Date", width: 110 },
    { field: "customer_name", headerName: "Customer Name", width: 180 },
    { field: "phone_number", headerName: "Phone Number", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: params => {
        let color;
        switch (params.value) {
          case "Open":
            color = "#e3f2fd"; // Light blue
            break;
          case "In Progress":
            color = "#fff3e0"; // Light orange
            break;
          case "Closed":
            color = "#e8f5e9"; // Light green
            break;
          default:
            color = "#f5f5f5"; // Light gray
        }
        return (
          <Chip
            label={params.value}
            size="small"
            style={{ backgroundColor: color }}
          />
        );
      },
    },
    { field: "status_feedback", headerName: "Status Feedback", width: 250 },
    { field: "note", headerName: "Feedback", width: 250 },
  ];

  // Transform the leads data into rows for the DataGrid
  const rows = displayLeads?.map(row => {
    const statusFeedback =
      row.status === "In Progress"
        ? row.salesLeadInProgressStatus
        : row.salesLeadClosedStatus;

    const date = sqlToStandardDate(row.created_at); // Convert SQL date to standard date format

    return {
      id: row.id,
      date,
      ticket_num: row.ticket_num,
      agent: row.dealer_code,
      address: row.address,
      alternative_number: row.alternative_number,
      phone_number: row.phone_number,
      customer_name: row.customer_name,
      ref_number: row.ref_number,
      agreed_to_terms: row.agreed_to_terms,
      supplier: row.supplier,
      platform: row.platform !== "App" ? "ussd" : row.platform,
      note: row.note,
      // category: row.category,
      status: row.status,
      status_feedback: statusFeedback,
      supporting_document_ref: row.supporting_document_ref,
      unread: row.unread,
      rating_star: row.rating_star,
      rating_comment: row.rating_comment,
    };
  });

  // Fetch leads data when the component mounts or when the Supplier prop changes
  useEffect(() => {
    getData();
  }, [Supplier]);

  useEffect(() => {
    const subscription = supabase
      .channel("sales_assist_activity_channel_3")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: process.env.REACT_APP_SB_SCHEMA,
          table: "sales_assist_activity",
          filter: `sales_assist_id.in.(${leads
            .map(lead => lead.id)
            .join(",")})`, // Only listen to current user's leads
        },
        () => getData(false)
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [viewLead.isOpen, addLeadModal, leads]); // Add leads dependency

  return (
    <Paper
      elevation={0}
      style={{
        padding: 24,
        borderRadius: 8,
        boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
        marginBottom: 24,
      }}
    >
      <Alert severity="info" style={{ marginBottom: 16, borderRadius: 4 }}>
        The ticket creation feature on Sales Assist now only handles
        lead-related queries. For all other query types, please use the upcoming
        Agent Support feature. You can still access and view your existing
        tickets and feedback.
      </Alert>

      <Grid
        container
        spacing={2}
        alignItems="center"
        style={{ marginBottom: 16 }}
      >
        <Grid item md={8}>
          <FormGroup row>
            <FormControlLabel
              label="Open"
              control={
                <Checkbox
                  color="primary"
                  checked={openFilter}
                  onClick={handleClick}
                  name="open"
                />
              }
            />
            <FormControlLabel
              label="In Progress"
              control={
                <Checkbox
                  color="primary"
                  checked={inProgressFilter}
                  onClick={handleClick}
                  name="inProgress"
                />
              }
            />
            <FormControlLabel
              label="Closed"
              control={
                <Checkbox
                  color="primary"
                  checked={closedFilter}
                  onClick={handleClick}
                  name="closed"
                />
              }
            />
          </FormGroup>
        </Grid>

        <Grid item md={4} container justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={toggleAddLeadModal}
            style={{ borderRadius: 20 }}
          >
            Add Lead
          </Button>
        </Grid>
      </Grid>

      <div style={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows || []}
          columns={columns}
          loading={loading}
          pageSize={10}
          disableSelectionOnClick
          autoHeight={false}
          density="standard"
        />
      </div>

      <ModalCard
        open={viewLead.isOpen}
        onClose={closeViewLead}
        closeButtonText="Close"
      >
        <ViewLead leadId={viewLead.id} />
      </ModalCard>

      <ModalCard open={addLeadModal} onClose={toggleAddLeadModal}>
        <AddLeadModal
          supplier={Supplier}
          onClose={toggleAddLeadModal}
          open={addLeadModal}
        />
      </ModalCard>

      {/* Modal for rating service */}
      <ModalCard open={rateService.isOpen}>
        <RateAgentAssistServiceModal
          ticketId={rateService.id}
          onClose={closeRateServiceModal}
          table="sales_assist"
        />
      </ModalCard>
    </Paper>
  );
}
